import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import FavoriteButton from "@/components/feature/FavoriteButton";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { getMovieById, getRecommendations } from "@/utils/movie";
import ShareMenu from "@/pages/detail/components/ShareMenu";
import MovieRating from "@/pages/detail/components/MovieRating";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;

  useDocumentTitle(
    movie ? `${movie.title} - 色色影院` : "影视详情 - 色色影院",
    movie ? `在色色影院查看《${movie.title}》的详细介绍及相关影视推荐。` : "影视详情页",
  );

  if (!movie) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
            <i className="ri-film-line text-3xl"></i>
          </span>
          <h1 className="mt-4 text-xl font-bold text-foreground-950">未找到该影视内容</h1>
          <p className="mt-2 text-sm text-foreground-500">
            你访问的作品可能已下线，去分类页看看其他内容吧。
          </p>
          <Link
            to="/category"
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-compass-3-line text-lg"></i>
            浏览影视分类
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const recommendations = getRecommendations(movie, 6);
  const infoItems = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join(" / ") },
    { label: "类型", value: movie.genres.join(" · ") },
    { label: "上映时间", value: movie.releaseDate },
    { label: "地区", value: movie.region },
    { label: "时长", value: movie.duration },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 md:px-6 md:py-8">
        <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link
            to={`/category?type=${movie.type}`}
            className="transition-colors hover:text-primary-700"
          >
            {movie.type === "movie" ? "电影" : "电视剧"}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">{movie.title}</span>
        </nav>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="overflow-hidden rounded-lg border border-background-200">
              <div className="relative w-full pb-[140%]">
                <img
                  src={movie.poster}
                  alt={`${movie.title}海报`}
                  title={`${movie.title} - 色色影院`}
                  className="absolute inset-0 h-full w-full object-cover object-top"
                />
                <span className="absolute left-3 top-3 rounded-md bg-primary-900/85 px-2.5 py-1 text-xs font-medium text-background-50">
                  {movie.type === "movie" ? "电影" : "电视剧"}
                </span>
              </div>
            </div>

            <div className="mt-4 flex gap-3">
              <div className="flex-1">
                <FavoriteButton movieId={movie.id} variant="solid" />
              </div>
              <ShareMenu />
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="mb-3 text-sm font-semibold text-foreground-900">影片资料</h2>
              <dl className="space-y-3 text-sm">
                {infoItems.map((item) => (
                  <div key={item.label} className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-500">{item.label}</dt>
                    <dd className="text-foreground-800">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-md bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-800">
                {movie.genres[0]}
              </span>
              <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-900">
                {movie.year}
              </span>
              <span className="flex items-center gap-1 text-sm font-bold text-accent-600">
                <i className="ri-star-fill"></i>
                {movie.rating.toFixed(1)}
              </span>
            </div>

            <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              {movie.title}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {movie.summary}
            </p>

            <div className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                <span className="h-5 w-1 rounded-full bg-primary-600"></span>
                剧情介绍
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {movie.synopsis}
              </p>
            </div>

            <div className="mt-8">
              <MovieRating rating={movie.rating} />
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={`/category?type=${movie.type}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                <i className="ri-arrow-left-line text-lg"></i>
                返回分类页
              </Link>
              <Link
                to="/"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                <i className="ri-home-4-line text-lg"></i>
                返回首页
              </Link>
            </div>
          </div>
        </div>

        <section className="mt-14">
          <SectionHeading
            title="相关推荐"
            subtitle="根据类型与主演匹配的更多作品"
            moreTo={`/category?type=${movie.type}`}
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {recommendations.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}